import { Button, Dropdown, DropdownMenu, DropdownMenuItem, EditIcon, ImportIcon, Row, Table } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import {
  FunctionalClassSubstitutionBehavior,
  TimetableReportType,
  use_TimetableBlocksQuery,
  use_UpdateClassesAttendancePlanShowInSubstitutionPlanMutation,
} from '../../../types/planung-graphql-client-defs';
import { createTimetableCollectiveReport, downloadReport } from '../../../utils/pdfReport';
import { showUserErrorToast } from '../../../utils/toast';
import { useClassesTable } from '../hooks/useClassesTable';
import { ClassesTableType } from '../types/ClassesTypes';

export const FunctionClassesTable: FC = observer(() => {
  const { t } = useTranslation();
  const { selectedSchoolYear } = useUserConfigContext();
  const context = useMemorizedCacheTag('CLASSES_FIXED');
  const timetableBlockContext = useMemorizedCacheTag('TIMETABLE_BLOCK');

  const [, updateAttendancePlanAndShowInPlan] = use_UpdateClassesAttendancePlanShowInSubstitutionPlanMutation();

  const today = dayjs().startOf('day').toISOString();

  const [switchLoading, setSwitchLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [{ data: timetableBlockData }] = use_TimetableBlocksQuery({
    variables: {
      where: {
        schoolYear: { uuid: selectedSchoolYear?.uuid },
        start_LTE: today,
        end_GTE: today,
      },
    },
    context: timetableBlockContext,
    pause: !selectedSchoolYear,
  });

  const {
    data,
    columns,
    tableSettings,
    tableSorting,
    tableSelection,
    tableVisibility,
    printerSettings,
    actionBarSettings,
  } = useClassesTable({
    context: 'function',
    columns: ['name', 'shortName', 'room', 'color', 'functionalClassSubstitutionBehavior'],
  });

  const functionClasses = data.filter((c) => c.functionClass && c.relevantForSubstitution);

  const currentTimetableBlockUuid = useMemo(() => {
    return timetableBlockData?.timetableBlocks[0]?.uuid;
  }, [timetableBlockData]);

  return (
    <>
      <Table<ClassesTableType>
        data={functionClasses}
        columns={columns}
        {...tableSettings}
        {...tableSelection}
        {...tableSorting}
        {...tableVisibility}
        printerSettings={{
          ...printerSettings,
          subline: `${t('functionClass.attendanceClass', { count: 2 })} - ${t('common.schoolYear')} ${selectedSchoolYear?.shortName}`,
          filename: `${t('functionClass.attendanceClass', { count: 2 })}_${selectedSchoolYear?.shortName}`,
        }}
        actionBarSettings={{
          ...actionBarSettings,
          showBulkEdit: false,
          extendedActionsRight: (
            <Button
              hierarchy='tertiary'
              disabled={!currentTimetableBlockUuid}
              isLoading={downloadLoading}
              onClick={() => {
                setDownloadLoading(true);
                if (currentTimetableBlockUuid) {
                  createTimetableCollectiveReport(
                    currentTimetableBlockUuid,
                    'timetable',
                    TimetableReportType.AttendanceSchedule,
                  )
                    .then(async (value) => {
                      void downloadReport(
                        {
                          uuid: value.uuid,
                          name: `${t('classes.currentAttendanceSchedule', { count: 1 })}-${dayjs().format('YYYY-MM-DD')}`,
                          contextUuid: 'is-ignored',
                          contextName: 'is-ignored',
                          timestamp: dayjs(),
                        },
                        TimetableReportType.AttendanceSchedule,
                      );
                    })
                    .catch((error) => {
                      showUserErrorToast({ error });
                    })
                    .finally(() => {
                      setDownloadLoading(false);
                    });
                }
              }}
              icon={<ImportIcon />}
            >
              {t('classes.currentAttendanceSchedule', { count: 1 })}
            </Button>
          ),
        }}
        lastCol={(row) => {
          return (
            <Dropdown trigger={<Button hierarchy='ghost' icon={<EditIcon />} />}>
              <DropdownMenu
                data={[
                  {
                    type: 'switch',
                    label: t('functionClass.showInSubstitutionPlan', { count: 1 }),
                    tooltip: t('functionClass.showInSubstitutionPlanTooltip'),
                    disabled: switchLoading,
                    onValueChange: async (value) => {
                      setSwitchLoading(true);
                      const isTrue = value as boolean;
                      await updateAttendancePlanAndShowInPlan(
                        {
                          uuids: [row.original.uuid],
                          functionalClassSubstitutionBehavior: isTrue
                            ? ('SHOW_IN_SUBSTITUTION_PLAN' as FunctionalClassSubstitutionBehavior)
                            : null,
                        },
                        context,
                      );
                      setSwitchLoading(false);
                    },
                    value: row.original.functionalClassSubstitutionBehavior === 'SHOW_IN_SUBSTITUTION_PLAN',
                  },
                  {
                    type: 'switch',
                    disabled: switchLoading,
                    tooltip: t('functionClass.normalClassBehaviorTooltip'),
                    label: t('functionClass.normalClassBehavior', { count: 1 }),
                    onValueChange: async (value) => {
                      setSwitchLoading(true);
                      const isTrue = value as boolean;
                      await updateAttendancePlanAndShowInPlan(
                        {
                          uuids: [row.original.uuid],
                          functionalClassSubstitutionBehavior: isTrue
                            ? ('NORMAL_CLASS_BEHAVIOR' as FunctionalClassSubstitutionBehavior)
                            : null,
                        },
                        context,
                      );
                      setSwitchLoading(false);
                    },
                    value: row.original.functionalClassSubstitutionBehavior === 'NORMAL_CLASS_BEHAVIOR',
                  },
                  {
                    type: 'switch',
                    disabled: switchLoading,
                    tooltip: t('functionClass.attendanceClassBehaviorTooltip'),
                    label: t('functionClass.attendanceClass', { count: 1 }),
                    onValueChange: async (value) => {
                      setSwitchLoading(true);
                      const isTrue = value as boolean;

                      await updateAttendancePlanAndShowInPlan(
                        {
                          uuids: [row.original.uuid],
                          functionalClassSubstitutionBehavior: isTrue
                            ? ('ATTENDANCE_PLAN_SOURCE' as FunctionalClassSubstitutionBehavior)
                            : null,
                        },
                        context,
                      );
                      setSwitchLoading(false);
                    },
                    value: row.original.functionalClassSubstitutionBehavior === 'ATTENDANCE_PLAN_SOURCE',
                  },
                ]}
              />
            </Dropdown>
          );
        }}
      />
    </>
  );
});
